import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { MenuItem, Spinner, InputGroup } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { getFieldDependentTable } from '../utils/metadata';
import { useDebounce } from "@uidotdev/usehooks";
import { useGlobalMetadata } from '../utils/providers';

// Type imports
import type { RelationshipLookup } from '../types/metadata';
import type { BaseRecord } from '../types/api';
import type { ItemPredicate, ItemRenderer } from '@blueprintjs/select';

import "./Common.css";
import RecordLink from './links/RecordLink';

// Blueprint Select component typed for BaseRecord
const RecordSelect = Select.ofType<BaseRecord>();

// Interface for component props
interface RecordLookupSelectProps {
  name: string;
  relationship: RelationshipLookup;
  record: BaseRecord | null;
  disabled?: boolean;
  title?: string;
  onChange: (record: BaseRecord | null) => void;
  routeName?: string;
  schema: string;
}

// Interface for Select modifiers (from Blueprint.js)
interface SelectModifiers {
  active: boolean;
  disabled: boolean;
  matchesPredicate: boolean;
}

// Interface for Select item renderer props
interface SelectItemProps {
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  modifiers: SelectModifiers;
}

function RecordLookupSelect({ 
  name, 
  relationship, 
  record, 
  disabled = false, 
  title, 
  onChange, 
  routeName, 
  schema 
}: RecordLookupSelectProps) {
  // Assertive null check: relationship MUST have valid target_table_name
  if (!relationship.target_table_name) {
    throw new Error(`Invalid relationship in RecordLookupSelect: missing target_table_name for field ${name}`);
  }

  const [records, setRecords] = useState<BaseRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, 300); // Debounce query with 300ms delay

  const { data: metadata } = useGlobalMetadata();

  const clientSideRecords = useMemo(() => {
    const rows = metadata?.clientSideSelectTables?.[relationship.target_table_name!];
    return Array.isArray(rows) ? rows as BaseRecord[] : null;
  }, [metadata?.clientSideSelectTables, relationship.target_table_name]);

  const tableLabel = metadata?.tableData?.byName?.[relationship.target_table_name]?.ui_label || relationship.target_table_name;
  
  const fetchRecords = useCallback(async (searchText: string) => {
    // Metadata-designated small tables are hydrated once with the application
    // metadata. Blueprint filters this in-memory array as the user types, so
    // opening or searching the dropdown never emits a PostgREST request.
    if (clientSideRecords) {
      setRecords(clientSideRecords);
      return;
    }
    if (!searchText) {
      setRecords([]);
      return;
    }
    setLoading(true);

    const conditions: Record<string, any> = {};
    conditions[relationship.target_label_field] = {
      filterType: 'string',
      type: 'contains',
      filter: searchText.toLowerCase(),
    };

    const { data, error } = await getFieldDependentTable<BaseRecord>({
      tableName: relationship.target_table_name!, // Already validated above
      schema,
      conditions,
      orderBy: {
        column: relationship.target_label_field,
        direction: 'ascending',
      },
      limitOffset: {
        limit: 100,
        offset: 0,
      },
    });

    if (!error && data) {
      setRecords(Array.isArray(data) ? data : [data]);
    } else {
      console.error(
        `Error fetching ${relationship.target_table_name} with conditions:`,
        conditions
      );
      setRecords([]);
    }
    setLoading(false);
  }, [clientSideRecords, relationship.target_label_field, relationship.target_table_name, schema]);

  // Fetch records when debouncedQuery changes
  useEffect(() => {
    fetchRecords(debouncedQuery);
  }, [debouncedQuery, fetchRecords]);

  const filterLoadedRecords: ItemPredicate<BaseRecord> = (query: string, record: BaseRecord) => {
    const searchValue = record[relationship.target_label_field];
    if (!searchValue) {
      return false;
    }
    return String(searchValue).toLowerCase().indexOf(query.toLowerCase()) >= 0;
  };

  const renderRecord: ItemRenderer<BaseRecord> = useCallback((record: BaseRecord, { handleClick, modifiers }: SelectItemProps) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    return (
      <MenuItem
        active={modifiers.active}
        disabled={modifiers.disabled}
        key={record.id}
        onClick={handleClick}
        text={record[relationship.target_label_field] || '< No Name! >'}
        roleStructure="listoption"
      />
    );
  }, [relationship.target_label_field]);

  const handleItemSelect = useCallback((selectedRecord: BaseRecord) => {
    setQuery(selectedRecord[relationship.target_label_field] || '');
    onChange(selectedRecord);
  }, [onChange, relationship.target_label_field]);

  const handleQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery);
  }, []);

  const effectiveValue = record ? String(record[relationship.target_label_field] || '') : '';
  const effectiveId = record ? record.id : '';

  if (disabled) {
    return (
      <InputGroup
        name={name}
        value={effectiveValue}
        readOnly
        disabled
        title={title}
      />
    );
  }

  return (
    <>
      <div
        className="record-link-container"
        style={{
          position: 'relative',
          width: '100%'
          /* NOTE: position: relative for RecordLink overlay - UNCERTAIN DECISION */
          /* If positioning issues arise, consider alternative layout (flex, grid) */
        }}
      >
        {effectiveValue && effectiveId && routeName && (
          <RecordLink
            tableName={relationship.target_table_name}
            recordId={effectiveId}
            className="record-link-overlay"
          />
        )}
        <RecordSelect
          items={records}
          itemPredicate={filterLoadedRecords}
          itemRenderer={renderRecord}
          onItemSelect={handleItemSelect}
          onQueryChange={handleQueryChange}
          query={query}
          noResults={
            <MenuItem
              disabled={true}
              text={loading ? 'Loading...' : 'No results.'}
              roleStructure="listoption"
            />
          }
          inputProps={{
            placeholder: `Search for a ${tableLabel}`,
          }}
          popoverProps={{
            minimal: true,
            matchTargetWidth: true,
            portalClassName: 'sky-elevated-portal',
          }}
        >
          <InputGroup
            name={name}
            value={effectiveValue}
            placeholder={`Select a ${tableLabel}`}
            rightElement={loading ? <Spinner size={16} /> : undefined}
            readOnly
          />
        </RecordSelect>
      </div>
    </>
  );
}

export default RecordLookupSelect;
